import {
  ChannelType,
  SEARCH_FILTERS,
  SEARCH_HAS_VALUES,
  selectDisplayName,
} from '@alternativamais/chat-core';
import { useMemo, useRef, useState } from 'react';

import {
  gravarPreferencia,
  lerPreferencia,
} from '../../../armazenamento/local';
import { Icone } from '../../../componentes/Icone';
import { useAtender } from '../../../chat/atalhos';
import { useCliente, useEstado } from '../../../chat/contexto';
import { nomeLimpo } from './resolver';

const RECENTES = 'buscas-recentes';

type Sugestao = {
  chave: string;
  titulo: string;
  descricao?: string;
  inserir: string;
};

/**
 * Campo "Buscar" do cabeçalho (C47): ao focar, os filtros do Discord e as
 * buscas recentes; escrevendo um filtro, as pessoas, canais ou tipos.
 */
export function CampoBusca({
  canalId,
  valor,
  aoBuscar,
}: {
  canalId: string;
  valor: string;
  aoBuscar: (texto: string) => void;
}) {
  const cliente = useCliente();
  const canal = useEstado((estado) => estado.channels[canalId]);
  const guildId = canal?.guild_id ?? null;
  const nomeServidor = useEstado((estado) =>
    guildId ? estado.guilds[guildId]?.name : undefined,
  );
  const [texto, setTexto] = useState(valor);
  const [aberto, setAberto] = useState(false);
  const [recentes, setRecentes] = useState<string[]>(() =>
    lerPreferencia<string[]>(RECENTES, []),
  );
  const campo = useRef<HTMLInputElement>(null);
  // Ctrl+F: vai para a busca.
  useAtender('buscar', () => campo.current?.focus());
  const [anterior, setAnterior] = useState(valor);
  if (valor !== anterior) {
    setAnterior(valor);
    setTexto(valor);
  }

  // O filtro sendo escrito no fim: `de:raf` → sugere pessoas.
  const filtroAtual = /(?:^|\s)(de|menciona|em|tem|fixada):\s*(\S*)$/i.exec(
    texto,
  );
  const sugestoes = useMemo<Sugestao[]>(() => {
    if (!filtroAtual) return [];
    const [inteiro, chave, termo] = filtroAtual;
    const base = texto.slice(0, texto.length - inteiro.length);
    const prefixo = `${base}${base && !base.endsWith(' ') ? ' ' : ''}${inteiro.trimStart().slice(0, chave.length + 1)}`;
    const busca = termo.toLowerCase();
    const estado = cliente.store.getState();
    if (chave === 'de' || chave === 'menciona') {
      const ids = guildId
        ? Object.keys(estado.members[guildId] ?? {})
        : (canal?.recipients ?? []).map((pessoa) => pessoa.id);
      return ids
        .map((id) => estado.users[id])
        .filter(
          (usuario) =>
            usuario &&
            (usuario.username.toLowerCase().includes(busca) ||
              selectDisplayName(estado, usuario.id, guildId)
                .toLowerCase()
                .includes(busca)),
        )
        .slice(0, 8)
        .map((usuario) => ({
          chave: usuario.id,
          titulo: selectDisplayName(estado, usuario.id, guildId),
          descricao: usuario.username,
          inserir: `${prefixo}${usuario.username} `,
        }));
    }
    if (chave === 'em' && guildId) {
      return (estado.guildChannelIds[guildId] ?? [])
        .map((id) => estado.channels[id])
        .filter(
          (item) =>
            item &&
            item.type !== ChannelType.GUILD_CATEGORY &&
            nomeLimpo(item.name ?? '')
              .toLowerCase()
              .includes(busca),
        )
        .slice(0, 8)
        .map((item) => ({
          chave: item.id,
          titulo: `#${item.name}`,
          inserir: `${prefixo}${nomeLimpo(item.name ?? '')} `,
        }));
    }
    if (chave === 'tem') {
      return [
        'imagem',
        'arquivo',
        'link',
        'vídeo',
        'som',
        'figurinha',
        'enquete',
        'embed',
      ]
        .filter((item) => item.startsWith(busca) && SEARCH_HAS_VALUES[item])
        .map((item) => ({
          chave: item,
          titulo: item,
          inserir: `${prefixo}${item} `,
        }));
    }
    if (chave === 'fixada') {
      return ['sim', 'não'].map((item) => ({
        chave: item,
        titulo: item,
        inserir: `${prefixo}${item} `,
      }));
    }
    return [];
  }, [filtroAtual, texto, cliente, guildId, canal]);

  const buscar = (valorDaBusca: string) => {
    const limpo = valorDaBusca.trim();
    setAberto(false);
    campo.current?.blur();
    aoBuscar(limpo);
    if (!limpo) return;
    const lista = [limpo, ...recentes.filter((item) => item !== limpo)].slice(
      0,
      5,
    );
    setRecentes(lista);
    gravarPreferencia(RECENTES, lista);
  };

  const escolher = (inserir: string) => {
    setTexto(inserir);
    requestAnimationFrame(() => campo.current?.focus());
  };

  const rotulo =
    guildId && nomeServidor ? `Buscar em ${nomeServidor}` : 'Buscar';

  return (
    <div className="relative mx-1">
      <label
        className={`flex h-7 items-center gap-1 rounded-md bg-rail px-2 text-texto-3 transition-[width] ${
          aberto || texto
            ? 'w-60 max-md:w-[min(240px,55vw)]'
            : 'w-[170px] max-md:w-8'
        }`}
      >
        <span className="sr-only">Buscar mensagens</span>
        <input
          ref={campo}
          value={texto}
          placeholder="Buscar"
          onChange={(evento) => setTexto(evento.target.value)}
          onFocus={() => setAberto(true)}
          onBlur={() => setAberto(false)}
          onKeyDown={(evento) => {
            if (evento.key === 'Enter') {
              evento.preventDefault();
              buscar(texto);
            } else if (evento.key === 'Escape') {
              evento.preventDefault();
              setAberto(false);
              campo.current?.blur();
            }
          }}
          className="h-7 min-w-0 grow border-0 bg-transparent text-sm text-texto outline-none placeholder:text-texto-3"
        />
        {texto ? (
          <button
            type="button"
            aria-label="Limpar busca"
            onMouseDown={(evento) => evento.preventDefault()}
            onClick={() => {
              setTexto('');
              aoBuscar('');
            }}
            className="flex text-texto-3 hover:text-texto"
          >
            <Icone nome="x" tamanho={16} />
          </button>
        ) : (
          <Icone nome="busca" tamanho={16} />
        )}
      </label>
      {aberto && (
        <div
          role="listbox"
          aria-label="Sugestões de busca"
          onMouseDown={(evento) => evento.preventDefault()}
          className="absolute top-full right-0 z-30 mt-2 flex w-80 max-w-[calc(100vw-24px)] flex-col rounded-xl border border-borda bg-elevado p-2 shadow-[0_12px_32px_rgba(0,0,0,.45)]"
        >
          <div className="px-2 pt-1 pb-2 text-xs font-semibold tracking-[.04em] text-texto-3 uppercase">
            {filtroAtual ? 'Sugestões' : rotulo}
          </div>
          {filtroAtual ? (
            sugestoes.length ? (
              sugestoes.map((sugestao) => (
                <button
                  key={sugestao.chave}
                  type="button"
                  role="option"
                  aria-selected={false}
                  onClick={() => escolher(sugestao.inserir)}
                  className="flex h-9 items-center gap-2 rounded-md px-2 text-left text-sm hover:bg-ativo"
                >
                  <span className="truncate font-semibold">
                    {sugestao.titulo}
                  </span>
                  {sugestao.descricao && (
                    <span className="truncate text-texto-3">
                      {sugestao.descricao}
                    </span>
                  )}
                </button>
              ))
            ) : (
              <p className="m-0 px-2 py-2 text-sm text-texto-3">
                Nada encontrado.
              </p>
            )
          ) : (
            <>
              <div className="px-2 pb-1 text-[11px] font-semibold tracking-[.04em] text-texto-3 uppercase">
                Filtros de busca
              </div>
              {SEARCH_FILTERS.filter(
                (filtro) => guildId || filtro.key !== 'em',
              ).map((filtro) => (
                <button
                  key={filtro.key}
                  type="button"
                  role="option"
                  aria-selected={false}
                  onClick={() =>
                    escolher(
                      `${texto}${texto && !texto.endsWith(' ') ? ' ' : ''}${filtro.key}:`,
                    )
                  }
                  className="flex h-8 items-center gap-2 rounded-md px-2 text-left text-sm hover:bg-ativo"
                >
                  <span className="font-semibold">{filtro.key}:</span>
                  <span className="truncate text-texto-3">
                    {filtro.description}
                  </span>
                </button>
              ))}
              {recentes.length > 0 && (
                <>
                  <div className="mt-2 flex items-center justify-between px-2 pt-2 pb-1 text-[11px] font-semibold tracking-[.04em] text-texto-3 uppercase">
                    Buscas recentes
                    <button
                      type="button"
                      onClick={() => {
                        setRecentes([]);
                        gravarPreferencia(RECENTES, []);
                      }}
                      className="tracking-normal normal-case text-link hover:underline"
                    >
                      Limpar
                    </button>
                  </div>
                  {recentes.map((item) => (
                    <button
                      key={item}
                      type="button"
                      role="option"
                      aria-selected={false}
                      onClick={() => {
                        setTexto(item);
                        buscar(item);
                      }}
                      className="flex h-8 items-center gap-2 rounded-md px-2 text-left text-sm hover:bg-ativo"
                    >
                      <Icone
                        nome="relogio"
                        tamanho={14}
                        className="shrink-0 text-texto-3"
                      />
                      <span className="truncate">{item}</span>
                    </button>
                  ))}
                </>
              )}
            </>
          )}
        </div>
      )}
    </div>
  );
}
